import React, { useState, useEffect } from 'react'
import { useAuthStore } from '../store/useAuthStore'
import { supabase } from '../lib/supabaseClient'
import { EmailService } from '../services/emailService'

const AppointmentBooking = () => {
  const { user, profile } = useAuthStore()
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedTime, setSelectedTime] = useState('')
  const [selectedService, setSelectedService] = useState('')
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  })
  const [availableSlots, setAvailableSlots] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [errors, setErrors] = useState({})

  const [services, setServices] = useState([{ id: 'consultation', name: 'Free Consultation', duration: 60, price: 0 }])

  const generateSlots = (dateStr, durationMinutes = 60) => {
    if (!dateStr) return []
    const d = new Date(dateStr + 'T00:00:00')
    // JS: 0=Sun, 5=Fri
    const isFriday = d.getDay() === 5
    if (isFriday) return []
    const slots = []
    // 10:00 to 17:00 inclusive start times for 60 min
    let start = new Date(dateStr + 'T10:00:00')
    const end = new Date(dateStr + 'T17:00:00')
    while (start <= end) {
      const hh = String(start.getHours()).padStart(2, '0')
      const mm = String(start.getMinutes()).padStart(2, '0')
      slots.push(`${hh}:${mm}`)
      start = new Date(start.getTime() + durationMinutes * 60000)
    }
    return slots
  }

  useEffect(() => {
    if (user && profile) {
      setFormData(prev => ({
        ...prev,
        name: profile.name || '',
        email: user.email || ''
      }))
    }
  }, [user, profile])

  // Load Free Consultation + other services from DB
  useEffect(() => {
    ;(async () => {
      try {
        const { data, error } = await supabase
          .from('services')
          .select('id,name')
          .order('created_at', { ascending: false })
        if (!error && Array.isArray(data)) {
          // Map DB services; default duration 60, price omitted
          const mapped = data
            .filter(s => (s?.name || '').toLowerCase().indexOf('consult') === -1) // exclude consultation if exists in DB; we'll add our fixed one
            .map(s => ({ id: s.id, name: s.name, duration: 60, price: 0 }))
          setServices([{ id: 'consultation', name: 'Free Consultation', duration: 60, price: 0 }, ...mapped])
        }
      } catch {}
    })()
  }, [])

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots()
    } else {
      setAvailableSlots([])
    }
  }, [selectedDate, selectedService])

  const fetchAvailableSlots = async () => {
    if (!selectedDate) return

    try {
      const { data, error } = await supabase
        .from('appointments')
        .select('time_slot')
        .eq('date', selectedDate)
        .in('status', ['pending','confirmed'])

      if (error) throw error

      const bookedSlots = (data || []).map(appointment => appointment.time_slot)
      const service = services.find(s => s.id === selectedService) || { duration: 60 }
      const allSlots = generateSlots(selectedDate, service.duration)
      const available = allSlots.filter(slot => !bookedSlots.includes(slot))
      setAvailableSlots(available)
    } catch (error) {
      console.error('Error fetching available slots:', error)
      // Fallback: show generated slots even if fetch failed
      const service = services.find(s => s.id === selectedService) || { duration: 60 }
      setAvailableSlots(generateSlots(selectedDate, service.duration))
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (!formData.name) newErrors.name = 'Name is required'
    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid'
    }
    if (!formData.phone) newErrors.phone = 'Phone number is required'
    if (!selectedService) newErrors.service = 'Please select a service'
    if (!selectedDate) newErrors.date = 'Please select a date'
    if (!selectedTime) newErrors.time = 'Please select a time'
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }
    
    setLoading(true)
    
    try {
      const { error } = await supabase
        .from('appointments')
        .insert({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          service: selectedService,
          date: selectedDate,
          time_slot: selectedTime,
          message: formData.message,
          status: 'pending',
          user_id: user?.id || null
        })

      if (error) throw error

      // Send notification email to admin
      try {
        await EmailService.sendAppointmentNotification({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          service: selectedService,
          date: selectedDate,
          time_slot: selectedTime,
          message: formData.message,
          status: 'pending'
        })
      } catch {}
      
      setSubmitted(true)
    } catch (error) {
      console.error('Error booking appointment:', error)
      if (error?.code === '23505') {
        setErrors({ submit: 'This time slot was just booked by someone else. Please choose another slot.' })
        try { await fetchAvailableSlots() } catch {}
      } else {
        setErrors({ submit: 'Failed to book appointment. Please try again.' })
      }
    } finally {
      setLoading(false)
    }
  }


  const getMinDate = () => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow.toISOString().split('T')[0]
  }

  const getMaxDate = () => {
    const maxDate = new Date()
    maxDate.setDate(maxDate.getDate() + 30)
    return maxDate.toISOString().split('T')[0]
  }

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <div className="bg-green-50 border border-green-200 rounded-lg p-8">
          <div className="text-green-600 text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-green-800 mb-4">Appointment Booked Successfully!</h2>
          <p className="text-green-700 mb-6">
            We've received your appointment request and will send you a confirmation email shortly.
          </p>
          <div className="bg-white rounded-lg p-4 text-left">
            <h3 className="font-semibold mb-2">Appointment Details:</h3>
            <p><strong>Service:</strong> {services.find(s => s.id === selectedService)?.name}</p>
            <p><strong>Date:</strong> {new Date(selectedDate).toLocaleDateString()}</p>
            <p><strong>Time:</strong> {selectedTime}</p>
            <p><strong>Duration:</strong> {services.find(s => s.id === selectedService)?.duration} minutes</p>
          </div>
          <button
            onClick={() => {
              setSubmitted(false)
              setSelectedDate('')
              setSelectedTime('')
              setSelectedService('')
              setFormData({ name: '', email: '', phone: '', message: '' })
            }}
            className="btn-primary mt-6"
          >
            Book Another Appointment
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Book an Appointment</h1>
        <p className="text-gray-600">
          Schedule a consultation with our team to discuss your project requirements
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Service Selection */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-6">Select Service</h2>
            <div className="space-y-3">
              {services.map((service) => (
                <label key={service.id} className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="service"
                    value={service.id}
                    checked={selectedService === service.id}
                    onChange={(e) => setSelectedService(e.target.value)}
                    className="mr-4"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{service.name}</div>
                    <div className="text-sm text-gray-600">
                      {service.duration} minutes • {service.price === 0 ? 'Free' : `৳${service.price}`}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            {errors.service && (
              <p className="error-text mt-2">{errors.service}</p>
            )}
          </div>

          {/* Date & Time Selection */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-6">Select Date & Time</h2>
            
            <div className="space-y-4">
              <div>
                <label className="label">Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value)
                    setSelectedTime('')
                  }}
                  min={getMinDate()}
                  max={getMaxDate()}
                  className="input-field"
                />
                {errors.date && (
                  <p className="error-text">{errors.date}</p>
                )}
              </div>

              {selectedDate && (
                <div>
                  <label className="label">Available Time Slots</label>
                  <div className="grid grid-cols-4 gap-2">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => setSelectedTime(slot)}
                        className={`p-2 text-sm rounded-lg border ${
                          selectedTime === slot
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-primary-500'
                        }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                  {availableSlots.length === 0 && (
                    <p className="text-sm text-gray-500 mt-2">No available slots for this date</p>
                  )}
                  {errors.time && (
                    <p className="error-text">{errors.time}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-6">Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Full Name</label>
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`input-field ${errors.name ? 'input-error' : ''}`}
                placeholder="Enter your full name"
              />
              {errors.name && (
                <p className="error-text">{errors.name}</p>
              )}
            </div>
            
            <div>
              <label className="label">Email</label>
              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className={`input-field ${errors.email ? 'input-error' : ''}`}
                placeholder="your.email@example.com"
              />
              {errors.email && (
                <p className="error-text">{errors.email}</p>
              )}
            </div>
            
            <div>
              <label className="label">Phone Number</label>
              <input
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className={`input-field ${errors.phone ? 'input-error' : ''}`}
                placeholder="+880 1234 567890"
              />
              {errors.phone && (
                <p className="error-text">{errors.phone}</p>
              )}
            </div>
          </div>
          
          <div className="mt-4">
            <label className="label">Message (Optional)</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="input-field"
              placeholder="Tell us about your project requirements..."
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="text-center">
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4">
              {errors.submit}
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading || !selectedService || !selectedDate || !selectedTime}
            className="btn-primary px-8 py-3 text-lg"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="loading-spinner mr-2"></div>
                Booking Appointment...
              </div>
            ) : (
              'Book Appointment'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default AppointmentBooking
